import { useState, useEffect } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { TipoProduto } from "@/types/tipo-produto";
import { tipoProdutosAdapter } from "@/data";
import { useQuery } from "@tanstack/react-query";

interface TipoProdutoComboboxProps {
  value?: string;
  onSelect: (produto: TipoProduto | null) => void;
  placeholder?: string;
}

export function TipoProdutoCombobox({
  value,
  onSelect,
  placeholder = "Selecione um produto...",
}: TipoProdutoComboboxProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const { data: produtosData } = useQuery({
    queryKey: ["tipo-produtos", search],
    queryFn: () =>
      tipoProdutosAdapter.list({
        search,
        filters: { ativo: true },
        limit: 100,
      }),
  });

  const produtos = produtosData?.items || [];
  const selectedProduto = produtos.find((p) => p.id_tipo_produto === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between"
        >
          {selectedProduto ? selectedProduto.nome : placeholder}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-full p-0">
        <Command>
          <CommandInput
            placeholder="Buscar produto..."
            value={search}
            onValueChange={setSearch}
          />
          <CommandEmpty>Nenhum produto encontrado.</CommandEmpty>
          <CommandGroup className="max-h-64 overflow-auto">
            {produtos.map((produto) => (
              <CommandItem
                key={produto.id_tipo_produto}
                value={produto.id_tipo_produto}
                onSelect={() => {
                  onSelect(produto);
                  setOpen(false);
                }}
              >
                <Check
                  className={cn(
                    "mr-2 h-4 w-4",
                    value === produto.id_tipo_produto ? "opacity-100" : "opacity-0"
                  )}
                />
                <div className="flex-1">
                  <div>{produto.nome}</div>
                  {produto.categoria && (
                    <div className="text-xs text-muted-foreground">{produto.categoria}</div>
                  )}
                </div>
              </CommandItem>
            ))}
          </CommandGroup>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
